import {
  HostAuthError,
  type HostRefreshReason,
  type HostToken,
  type HostTokenProfile,
  type HostTokenRequester,
} from "@healthos/auth/react";
import { absoluteApiUrl } from "../api/healthos";

/**
 * URL fragment key carrying a one-time impersonation code from the admin
 * console's "Log in as user" flow: `#impersonate=<code>`.
 *
 * A fragment, not a query param, because it never leaves the browser: it is
 * dropped before the request line of any HTTP request and never reaches
 * server access logs, proxies, or Referer headers on a further navigation.
 * It is still stripped from history immediately after being read (see
 * {@link detectAndConsumeImpersonationCode}) so the one-time code cannot
 * linger in browser history, tab restore, or a shared/screen-recorded URL bar
 * past the initial load.
 */
const IMPERSONATION_FRAGMENT_KEY = "impersonate";

function parseFragmentParams(hash: string): URLSearchParams {
  return new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
}

/**
 * Pure fragment parser: given a `location.hash` string, returns the
 * impersonation code or null. Split from window access so it is exhaustively
 * unit-testable, mirroring `evaluateHostAuth` in `./host-auth.ts`.
 */
export function evaluateImpersonationCode(hash: string): string | null {
  if (!hash) return null;
  const code = parseFragmentParams(hash).get(IMPERSONATION_FRAGMENT_KEY);
  return code && code.length > 0 ? code : null;
}

function stripImpersonationFragment(): void {
  const { pathname, search, hash } = window.location;
  const params = parseFragmentParams(hash);
  params.delete(IMPERSONATION_FRAGMENT_KEY);
  const remaining = params.toString();
  const nextUrl = `${pathname}${search}${remaining ? `#${remaining}` : ""}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

/**
 * Detect an impersonation code in the current URL fragment and immediately
 * strip it from the URL. Returns null (and leaves the URL untouched) when
 * there is no code.
 */
export function detectAndConsumeImpersonationCode(): string | null {
  if (typeof window === "undefined") return null;
  const code = evaluateImpersonationCode(window.location.hash);
  if (code) stripImpersonationFragment();
  return code;
}

export type RedeemedImpersonationToken = {
  accessToken: string;
  expiresAt: number;
  profile?: HostTokenProfile;
};

export type ImpersonationCodeRedeemer = (code: string) => Promise<RedeemedImpersonationToken>;

/**
 * Exchange a one-time impersonation code for a real access token via
 * core-api's `POST /impersonation/redeem`. No bearer token is
 * sent, and none is expected: the code itself is the credential. The raw
 * access token is returned only in this same-origin JSON response body,
 * never in a URL - only the opaque one-time code ever appeared there (see
 * {@link detectAndConsumeImpersonationCode}).
 */
export async function redeemImpersonationCode(code: string): Promise<RedeemedImpersonationToken> {
  const res = await fetch(absoluteApiUrl("/impersonation/redeem"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    throw new Error(`redeemImpersonationCode: redeem failed with status ${res.status}`);
  }
  const body = (await res.json()) as {
    accessToken: string;
    expiresAt: number;
    profile: { sub: string; name?: string };
  };
  return { accessToken: body.accessToken, expiresAt: body.expiresAt, profile: body.profile };
}

/**
 * Detect + redeem an impersonation code at boot. When no code is present,
 * this makes no network call and has no side effects (the URL fragment, if
 * any, is left untouched).
 *
 * `redeem` defaults to {@link redeemImpersonationCode} and is otherwise
 * overridable so this is exhaustively unit-testable without a live backend.
 *
 * Never rejects. A redemption failure (an expired or already-used code, or the
 * feature being disabled server-side) is swallowed to null rather than thrown,
 * so a boot sequence that awaits this can fall through to the normal
 * desktop/Teams/standalone path instead of leaving the app on a blank screen
 * behind an unhandled rejection.
 */
export async function initImpersonation(
  redeem: ImpersonationCodeRedeemer = redeemImpersonationCode,
): Promise<HostToken | null> {
  const code = detectAndConsumeImpersonationCode();
  if (!code) return null;
  try {
    const redeemed = await redeem(code);
    return {
      accessToken: redeemed.accessToken,
      expiresAt: redeemed.expiresAt,
      profile: redeemed.profile,
    };
  } catch (err) {
    console.warn("[impersonation] code redemption failed; continuing without it:", err);
    return null;
  }
}

/**
 * Build the `HostTokenRequester` for an impersonation session. There is no
 * refresh token, and no way to mint a new one: the code was one-time and is
 * already spent. The requester simply resolves the same token while it is
 * still valid, and rejects with `HostAuthError('refresh_failed')` once past
 * expiry - the existing host-auth degradation path (proactive-refresh
 * swallow, then a clean `hostError` screen on the next 401) already does the
 * right thing from there. See `packages/auth/src/react/hostAuth.ts` and
 * `token.ts` for that machinery.
 */
export function createImpersonationTokenRequester(token: HostToken): HostTokenRequester {
  return (_reason?: HostRefreshReason) => {
    const stillValid = token.expiresAt === null || Date.now() < token.expiresAt * 1000;
    return stillValid
      ? Promise.resolve(token)
      : Promise.reject(new HostAuthError("refresh_failed"));
  };
}
